// src/App.js
import React, { useState, useEffect, useCallback } from 'react';
import { Container, Typography, Button, Box, Grid, Alert, TextField, Paper } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { v4 as uuidv4 } from 'uuid';
import { format, startOfDay, getHours, getMinutes } from 'date-fns';
import { loadSites, saveSites } from './utils/storage';
import { formatDateToDayString, parseDayString, generateTimeLabelsForDay, isSameDay } from './utils/dateFormatter';

import CompanyList from './components/CompanyList';
import CompanyForm from './components/CompanyForm';
import DataChart from './components/DataChart';

// Initial demo data if local storage is empty
const initialDemoCompanies = [
  {
    id: uuidv4(),
    name: 'Meerland',
    adres: 'Frankweg 53',
    woonplaats: 'Nieuw Vennep',
    contactpersoon: 'Peter Bokhorst',
    opmerkingen: 'Landbouwbedrijf met koelcellen',
    sites: [
      {
        id: uuidv4(),
        naamLocatie: 'Phase 1',
        eanCode: '871000000000000001',
        aansluitdienst: '80A',
        leveringGTV: 18.4,
        vermogenBeperktTot: 18.4,
        terugleveringGTV: 18.4,
        csvData: []
      },
      {
        id: uuidv4(),
        naamLocatie: 'Phase 2',
        eanCode: '871000000000000002',
        aansluitdienst: '80A',
        leveringGTV: 18.4,
        vermogenBeperktTot: 18.4,
        terugleveringGTV: 18.4,
        csvData: []
      },
      {
        id: uuidv4(),
        naamLocatie: 'Phase 3',
        eanCode: '871000000000000003',
        aansluitdienst: '80A',
        leveringGTV: 18.4,
        vermogenBeperktTot: 18.4,
        terugleveringGTV: 18.4,
        csvData: []
      }
    ]
  },
  {
    id: uuidv4(),
    name: 'DJMiddelkoop',
    adres: 'Lireweg 3',
    woonplaats: 'Nieuw Vennep',
    contactpersoon: 'Rob Middelkoop',
    opmerkingen: '',
    sites: [
      {
        id: uuidv4(),
        naamLocatie: 'Main Location',
        eanCode: '871000000000000004',
        aansluitdienst: '80A',
        leveringGTV: 18.4,
        vermogenBeperktTot: 18.4,
        terugleveringGTV: 18.4,
        csvData: []
      }
    ]
  },
  {
    id: uuidv4(),
    name: 'CTS',
    adres: 'Schillingweg 50',
    woonplaats: 'Nieuw Vennep',
    contactpersoon: 'Edwin Beentjes',
    opmerkingen: '',
    sites: [
      {
        id: uuidv4(),
        naamLocatie: 'Main Location',
        eanCode: '871000000000000005',
        aansluitdienst: '80A',
        leveringGTV: 18.4,
        vermogenBeperktTot: 18.4,
        terugleveringGTV: 18.4,
        csvData: []
      }
    ]
  }
];

function App() {
  const [companies, setCompanies] = useState(initialDemoCompanies);
  const [selectedSiteIds, setSelectedSiteIds] = useState([]);
  const [isCompanyFormOpen, setIsCompanyFormOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);

  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  
  const [chartData, setChartData] = useState(null);
  const [simulationError, setSimulationError] = useState('');

  // Save companies to localStorage whenever they change
  useEffect(() => {
    saveSites(companies);
  }, [companies]);

  // Update available dates when selected sites change
  useEffect(() => {
    if (selectedSiteIds.length === 0) {
      setAvailableDates([]);
      setSelectedDate(null);
      return;
    }

    const dates = new Set();
    companies.forEach(company => {
      company.sites.forEach(site => {
        if (selectedSiteIds.includes(site.id)) {
          site.csvData.forEach(csv => {
            csv.data.forEach(row => {
              dates.add(formatDateToDayString(row.datetime));
            });
          });
        }
      });
    });
    const sortedDates = Array.from(dates).sort();
    setAvailableDates(sortedDates);
    if (sortedDates.length > 0 && (!selectedDate || !sortedDates.includes(formatDateToDayString(selectedDate)))) {
      setSelectedDate(parseDayString(sortedDates[0]));
    } else if (sortedDates.length === 0) {
      setSelectedDate(null);
    }
  }, [selectedSiteIds, companies, selectedDate]);

  const handleCompanySelect = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    if (!company) return;

    const companySiteIds = company.sites.map(site => site.id);
    const allSelected = companySiteIds.every(id => selectedSiteIds.includes(id));

    if (allSelected) {
      // If all sites are selected, deselect all sites from this company
      setSelectedSiteIds(prev => prev.filter(id => !companySiteIds.includes(id)));
    } else {
      // If not all sites are selected, select all sites from this company
      setSelectedSiteIds(prev => [...new Set([...prev, ...companySiteIds])]);
    }
    setChartData(null);
    setSimulationError('');
  };

  const handleSiteSelect = (siteId) => {
    setSelectedSiteIds(prev =>
      prev.includes(siteId) ? prev.filter(id => id !== siteId) : [...prev, siteId]
    );
    setChartData(null);
    setSimulationError('');
  };

  const handleOpenCompanyForm = (company = null) => {
    setEditingCompany(company);
    setIsCompanyFormOpen(true);
  };

  const handleCloseCompanyForm = () => {
    setIsCompanyFormOpen(false);
    setEditingCompany(null);
  };

  const handleSaveCompany = (companyData) => {
    setCompanies(prevCompanies => {
      const existingCompanyIndex = prevCompanies.findIndex(c => c.id === companyData.id);
      if (existingCompanyIndex > -1) {
        const updatedCompanies = [...prevCompanies];
        updatedCompanies[existingCompanyIndex] = companyData;
        return updatedCompanies;
      }
      return [...prevCompanies, companyData];
    });
  };

  const handleAddCsvToSite = (siteId, csvFile) => {
    // Add the CSV file to the site
    setCompanies(prevCompanies =>
      prevCompanies.map(company => ({
        ...company,
        sites: company.sites.map(site =>
          site.id === siteId
            ? { ...site, csvData: [...site.csvData, csvFile] }
            : site
        )
      }))
    );

    // Select the site if it's not already selected
    if (!selectedSiteIds.includes(siteId)) {
      setSelectedSiteIds(prev => [...prev, siteId]);
    }
  };

  const handleDeleteCsvFromSite = (siteId, csvId) => {
    setCompanies(prevCompanies =>
      prevCompanies.map(company => ({
        ...company,
        sites: company.sites.map(site =>
          site.id === siteId
            ? { ...site, csvData: site.csvData.filter(csv => csv.id !== csvId) }
            : site
        )
      }))
    );
  };

  const handleSimulate = useCallback(() => {
    setSimulationError('');
    if (selectedSiteIds.length === 0 || !selectedDate) {
      setSimulationError("Please select at least one site and a date.");
      setChartData(null);
      return;
    }

    const timeLabels = generateTimeLabelsForDay();
    const numIntervals = timeLabels.length;

    const aggregatedConsumption = new Array(numIntervals).fill(0);
    let totalLeveringGTV = 0;
    let totalTerugleveringGTV = 0;
    let dataFoundForDate = false;

    // Get all selected sites from all companies
    const selectedSites = companies.flatMap(company => 
      company.sites.filter(site => selectedSiteIds.includes(site.id))
    );

    selectedSites.forEach(site => {
      totalLeveringGTV += site.leveringGTV || 0;
      totalTerugleveringGTV += site.terugleveringGTV || 0;

      site.csvData.forEach(csv => {
        csv.data.forEach(row => {
          if (isSameDay(row.datetime, selectedDate)) {
            dataFoundForDate = true;
            const hour = getHours(row.datetime);
            if (hour >= 0 && hour < numIntervals) {
              aggregatedConsumption[hour] += row.value;
            }
          }
        });
      });
    });
    
    if (!dataFoundForDate && selectedSiteIds.length > 0) {
      setSimulationError(`No consumption data found for the selected sites on ${formatDateToDayString(selectedDate)}.`);
      setChartData({ labels: timeLabels, datasets: [] });
      return;
    }

    // Calculate the values according to the specified formulas
    const usageData = aggregatedConsumption;
    const freeDeliveryCapacityData = aggregatedConsumption.map(usage => {
      if (usage > 0) {
        return totalLeveringGTV - usage;
      } else {
        return totalLeveringGTV;
      }
    });
    const freeReturnDeliveryCapacityData = aggregatedConsumption.map(usage => {
      if (usage > 0) {
        return -totalTerugleveringGTV;
      } else if (usage < 0) {
        return -totalTerugleveringGTV - usage;
      } else {
        return -totalTerugleveringGTV;
      }
    });

    // Calculate totals for the summary widgets
    const totalUsage = usageData.reduce((sum, val) => sum + val, 0);
    const totalFreeDelivery = freeDeliveryCapacityData.reduce((sum, val) => sum + val, 0);
    const totalFreeReturn = freeReturnDeliveryCapacityData.reduce((sum, val) => sum + val, 0);

    setChartData({
      labels: timeLabels,
      datasets: [
        {
          label: 'Usage (kW)',
          data: usageData,
          borderColor: '#6E94A3',
          backgroundColor: 'rgba(110, 148, 163, 0.4)',
          fill: true,
          stack: 'stack0',
          order: 1,
        },
        {
          label: 'Free Delivery Capacity (kW)',
          data: freeDeliveryCapacityData,
          borderColor: '#D98856',
          backgroundColor: 'rgba(217, 136, 86, 0.4)',
          fill: true,
          stack: 'stack0',
          order: 2,
        },
        {
          label: 'Free Return Delivery Capacity (kW)',
          data: freeReturnDeliveryCapacityData,
          borderColor: '#A2C672',
          backgroundColor: 'rgba(162, 198, 114, 0.4)',
          fill: true,
          stack: 'stack0',
          order: 3,
        }
      ],
      summaryData: {
        totalUsage,
        totalFreeDelivery,
        totalFreeReturn
      }
    });
  }, [selectedSiteIds, companies, selectedDate]);

  const getSelectedSiteNames = () => {
    if (selectedSiteIds.length === 0) return "N/A";
    if (selectedSiteIds.length === 1) {
      const site = companies.flatMap(c => c.sites).find(s => s.id === selectedSiteIds[0]);
      return site ? site.naamLocatie : "N/A";
    }
    return `${selectedSiteIds.length} sites`;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ 
        minHeight: '100vh',
        bgcolor: '#F8F8FA',
        fontFamily: 'Roboto, Helvetica, Arial, sans-serif'
      }}>
        <Box sx={{ 
          py: 3, 
          px: { xs: 2, sm: 4, md: 6 },
        }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
            <img 
              src="/UnetiLabs-logo.png" 
              alt="UnetiLabs Logo" 
              style={{ 
                height: '60px', 
                marginBottom: '24px' 
              }} 
            />
            <Typography 
              variant="h4" 
              component="h1" 
              gutterBottom 
              sx={{ 
                mb: 4,
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: 700
              }}
            >
              Nieuw-Vennep Zuid - Grid Simulation
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                maxWidth: '800px',
                textAlign: 'center',
                mb: 4,
                color: 'text.secondary'
              }}
            >
              This simulator analyzes grid capacity for Nieuw-Vennep Zuid.
Select companies and upload their CSV data to visualize power usage, and simulate available delivery and return capacity.
The visualization shows usage patterns and available grid capacity throughout the day.
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <CompanyList
                companies={companies}
                selectedSiteIds={selectedSiteIds}
                onCompanySelect={handleCompanySelect}
                onSiteSelect={handleSiteSelect}
                onEditCompany={handleOpenCompanyForm}
                onAddCsv={handleAddCsvToSite}
                onDeleteCsv={handleDeleteCsvFromSite}
              />
            </Box>

            {selectedSiteIds.length > 0 && (
              <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 4 }}>Select Period</Typography>
                {selectedSiteIds.some(siteId => {
                  const site = companies.flatMap(c => c.sites).find(s => s.id === siteId);
                  return site && site.csvData.length === 0;
                }) && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Some selected sites have no CSV data. Please upload CSV files to view consumption data.
                  </Alert>
                )}
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <DatePicker
                    label="Select Simulation Date"
                    value={selectedDate}
                    onChange={(newValue) => {
                      setSelectedDate(newValue ? startOfDay(newValue) : null);
                      setChartData(null);
                      setSimulationError('');
                    }}
                    shouldDisableDate={(date) => {
                      const dateString = formatDateToDayString(date);
                      return !availableDates.includes(dateString);
                    }}
                    format="dd-MM-yyyy"
                    renderInput={(params) => <TextField {...params} helperText="Only dates with data for selected sites are enabled." />}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSimulate}
                    disabled={!selectedDate || selectedSiteIds.length === 0}
                  >
                    Simulate
                  </Button>
                </Box>
              </Paper>
            )}
          </Box>

          {simulationError && <Alert severity="warning" sx={{mb:2}}>{simulationError}</Alert>}
          {selectedSiteIds.length > 0 && selectedDate && chartData && (
            <Box sx={{ mt: 2 }}>
              <DataChart 
                chartData={chartData} 
                siteName={getSelectedSiteNames()}
                selectedDate={selectedDate ? format(selectedDate, 'dd-MM-yyyy') : ''}
              />
              <Paper elevation={3} sx={{ p: 2, mb: 2, mt:4 }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                  Summary
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Paper 
                      elevation={3} 
                      sx={{ 
                        p: 3, 
                        textAlign: 'center',
                        bgcolor: 'background.paper',
                        border: '1px solid rgba(0, 0, 0, 0.12)'
                      }}
                    >
                      <Typography variant="h6" sx={{ color: '#6E94A3' }} gutterBottom>
                        Total Usage
                      </Typography>
                      <Typography variant="h4">
                        {chartData.summaryData.totalUsage.toFixed(0)} kW
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper 
                      elevation={3} 
                      sx={{ 
                        p: 3, 
                        textAlign: 'center',
                        bgcolor: 'background.paper',
                        border: '1px solid rgba(0, 0, 0, 0.12)'
                      }}
                    >
                      <Typography variant="h6" sx={{ color: '#D98856' }} gutterBottom>
                        Total Free Delivery Capacity
                      </Typography>
                      <Typography variant="h4">
                        {chartData.summaryData.totalFreeDelivery.toFixed(0)} kW
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper 
                      elevation={3} 
                      sx={{ 
                        p: 3, 
                        textAlign: 'center',
                        bgcolor: 'background.paper',
                        border: '1px solid rgba(0, 0, 0, 0.12)'
                      }}
                    >
                      <Typography variant="h6" sx={{ color: '#A2C672' }} gutterBottom>
                        Total Free Return Capacity
                      </Typography>
                      <Typography variant="h4">
                        {chartData.summaryData.totalFreeReturn.toFixed(0)} kW
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          )}

          <CompanyForm
            open={isCompanyFormOpen}
            onClose={handleCloseCompanyForm}
            onSave={handleSaveCompany}
            companyToEdit={editingCompany}
          />
        </Box>
      </Box>
    </LocalizationProvider>
  );
}

export default App;